import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, IconButton, Tooltip, Alert, Stack, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Add, Edit, Delete, AssignmentInd, Person } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import {
  useUsers, useCreateUser, useUpdateUser, useDeleteUser,
  useRoles, useAssignRole, useRemoveUserRole,
  User, Role,
} from '../../hooks/useUsers'
import { format } from 'date-fns'

interface UserRow extends User { roles?: string[] }

const emptyForm = { name: '', email: '', password: '' }

export default function UsersPage() {
  const { t } = useTranslation()
  const { data: users = [], isLoading } = useUsers()
  const { data: roles = [] } = useRoles()
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const deleteUser = useDeleteUser()
  const assignRole = useAssignRole()
  const removeUserRole = useRemoveUserRole()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<UserRow | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  const [roleDialog, setRoleDialog] = useState<UserRow | null>(null)
  const [pickedRoleId, setPickedRoleId] = useState('')

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true) }
  const openEdit   = (u: UserRow) => { setEditing(u); setForm({ name: u.name, email: u.email, password: '' }); setDialogOpen(true) }

  const handleSubmit = async () => {
    if (editing) {
      await updateUser.mutateAsync({ id: editing.id, name: form.name })
    } else {
      if (form.password.length < 8) return
      await createUser.mutateAsync(form)
      analytics.track(EVENTS.USER_INVITED, { user_email: form.email })
    }
    setDialogOpen(false)
  }

  const openAssign = (u: UserRow) => {
    setRoleDialog(u)
    setPickedRoleId('')
  }
  const handleAssign = async () => {
    if (!roleDialog || !pickedRoleId) return
    await assignRole.mutateAsync({ userId: roleDialog.id, roleId: pickedRoleId })
    setPickedRoleId('')
  }
  const handleRemoveRole = async (userId: string, roleName: string) => {
    const r = roles.find((r) => r.name === roleName)
    if (!r) return
    await removeUserRole.mutateAsync({ userId, roleId: (r as Role).id })
  }

  const columns: GridColDef[] = [
    {
      field: 'name', headerName: t('common.name'), flex: 1, minWidth: 160,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person sx={{ fontSize: 18, color: '#94a3b8' }} />
          <span>{row.name}</span>
        </Box>
      ),
    },
    { field: 'email', headerName: t('auth.email'), flex: 1, minWidth: 200 },
    {
      field: 'roles', headerName: t('dashboard.roles'), flex: 1, minWidth: 220,
      renderCell: ({ row }) => {
        const list: string[] = row.roles ?? []
        if (list.length === 0) return <Chip label={t('users.noRoles')} size="small" variant="outlined" />
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {list.map((r) => (
              <Chip
                key={r}
                label={r}
                size="small"
                onDelete={() => handleRemoveRole(row.id, r)}
                sx={{ fontSize: 11, fontWeight: 600, bgcolor: '#EBF6FD', color: '#34A8E0', border: '1px solid #B3DCF2' }}
              />
            ))}
          </Stack>
        )
      },
    },
    {
      field: 'status', headerName: t('common.status'), width: 110,
      renderCell: ({ value }) => (
        <Chip label={value ?? 'active'} size="small" color={value === 'active' ? 'success' : 'default'} />
      ),
    },
    {
      field: 'created_at', headerName: t('device.created'), width: 130,
      valueFormatter: (v: string) => (v ? format(new Date(v), 'PP') : '—'),
    },
    {
      field: 'actions', headerName: t('common.actions'), width: 150, sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title={t('users.assignRole')}>
            <IconButton size="small" onClick={() => openAssign(row)}><AssignmentInd fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.id)}><Delete fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <PageHeader
        title={t('users.title')}
        subtitle={t('users.addUser')}
        breadcrumbs={[{ label: t('nav.identity') }, { label: t('users.title') }]}
        action={<Button variant="contained" startIcon={<Add />} onClick={openCreate}>{t('users.addUser')}</Button>}
      />

      {createUser.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to create user — check email format and that the password is at least 8 characters.
        </Alert>
      )}

      <DataGrid
        rows={users as UserRow[]}
        columns={columns}
        loading={isLoading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? t('users.editUser') : t('users.addUser')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label={t('common.name')} value={form.name} required fullWidth autoFocus
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          {!editing && (
            <>
              <TextField label={t('auth.email')} type="email" required fullWidth
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
              <TextField
                label={t('auth.password')}
                type="password"
                required
                fullWidth
                value={form.password}
                helperText="At least 8 characters — the user will use this to log in."
                error={form.password.length > 0 && form.password.length < 8}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit}
            disabled={
              createUser.isPending || updateUser.isPending || !form.name ||
              (!editing && (!form.email || form.password.length < 8))
            }>
            {editing ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign role dialog */}
      <Dialog open={!!roleDialog} onClose={() => setRoleDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('users.assignRole')} — {roleDialog?.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('dashboard.roles')}</InputLabel>
            <Select label={t('dashboard.roles')} value={pickedRoleId} onChange={(e) => setPickedRoleId(e.target.value as string)}>
              {(roles as Role[]).map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog(null)}>{t('common.close')}</Button>
          <Button variant="contained" disabled={!pickedRoleId || assignRole.isPending} onClick={handleAssign}>
            {t('users.assignRole')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('common.delete')}
        message={t('users.confirmDelete')}
        onConfirm={() => { deleteUser.mutate(deleteTarget!); setDeleteTarget(null) }}
        onClose={() => setDeleteTarget(null)}
        loading={deleteUser.isPending}
      />
    </Box>
  )
}
